import { useCallback, useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react';
import { DiagnosticReport, Patient } from '@medplum/fhirtypes';
import {
  PreImplantationFormState,
  UsePreImplantationChecksResult,
} from '../utils/medical-history.types';
import {
  fetchPreImplantationChecks,
  savePreImplantationCheck,
  deletePreImplantationCheck,
} from '../utils/pre-implantation.utils';

export const usePreImplantationChecks = (
  patient: Patient | undefined,
  encounterId?: string | null
): UsePreImplantationChecksResult => {
  const medplum = useMedplum();

  const [checks, setChecks] = useState<DiagnosticReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!patient) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const reports = await fetchPreImplantationChecks(medplum, patient);
      setChecks(reports);
    } catch (err) {
      console.error('Error fetching pre-implantation checks:', err);
      setError(
        err instanceof Error
          ? err
          : new Error('Failed to fetch pre-implantation checks')
      );
    } finally {
      setLoading(false);
    }
  }, [medplum, patient]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addCheck = useCallback(
    async (formState: PreImplantationFormState): Promise<DiagnosticReport> => {
      if (!patient) {
        throw new Error('Patient is required');
      }

      const result = await savePreImplantationCheck({
        medplum,
        patient,
        encounterId,
        formState,
      });

      await fetchData();
      return result;
    },
    [medplum, patient, encounterId, fetchData]
  );

  const updateCheck = useCallback(
    async (
      id: string,
      formState: PreImplantationFormState
    ): Promise<DiagnosticReport> => {
      if (!patient) {
        throw new Error('Patient is required');
      }

      const existingReport = checks.find((c) => c.id === id);
      if (!existingReport) {
        throw new Error('Report not found');
      }

      const result = await savePreImplantationCheck({
        medplum,
        patient,
        encounterId,
        formState,
        existingReport,
      });

      await fetchData();
      return result;
    },
    [medplum, patient, encounterId, checks, fetchData]
  );

  const deleteCheck = useCallback(
    async (id: string): Promise<void> => {
      const report = checks.find((c) => c.id === id);
      if (!report) {
        throw new Error('Report not found');
      }

      await deletePreImplantationCheck(medplum, report);
      await fetchData();
    },
    [medplum, checks, fetchData]
  );

  return {
    checks,
    loading,
    error,
    addCheck,
    updateCheck,
    deleteCheck,
    refetch: fetchData,
  };
};
